import { PublicClientApplication, Configuration, LogLevel } from "@azure/msal-browser";

// Runtime auth config fetched from backend (App Settings)
let runtimeConfig: { authority: string; clientId: string; apiScope: string } | null = null;
let configPromise: Promise<{ authority: string; clientId: string; apiScope: string }> | null = null;

/**
 * Fetch Entra auth config from the backend API.
 * Values come from Azure App Settings — no rebuild needed to change them.
 * Falls back to VITE_ env vars if the API call fails.
 */
export async function fetchAuthConfig(): Promise<{ authority: string; clientId: string; apiScope: string }> {
  if (runtimeConfig) return runtimeConfig;
  if (configPromise) return configPromise as Promise<{ authority: string; clientId: string; apiScope: string }>;

  configPromise = (async () => {
    try {
      const apiUrl = import.meta.env.VITE_ADMIN_API_URL || "";
      const res = await fetch(`${apiUrl}/auth/config`, { cache: "no-store" });
      if (res.ok) {
        const data = await res.json();
        runtimeConfig = {
          authority: data.entraAuthority || import.meta.env.VITE_ENTRA_AUTHORITY || "",
          clientId: data.entraClientId || import.meta.env.VITE_ENTRA_CLIENT_ID || "",
          apiScope: data.entraApiScope || import.meta.env.VITE_ENTRA_API_SCOPE || "api://89bd2fb7-7020-4cfe-a153-1c1ee37903c2/access_as_user",
        };
        console.debug("[Auth] Loaded Entra config from API:", runtimeConfig.authority);
        return runtimeConfig;
      }
    } catch (e) {
      console.warn("[Auth] Failed to fetch auth config from API, using build-time defaults", e);
    }
    // Fallback to build-time env vars
    runtimeConfig = {
      authority: import.meta.env.VITE_ENTRA_AUTHORITY || "",
      clientId: import.meta.env.VITE_ENTRA_CLIENT_ID || "",
      apiScope: import.meta.env.VITE_ENTRA_API_SCOPE || "api://89bd2fb7-7020-4cfe-a153-1c1ee37903c2/access_as_user",
    };
    return runtimeConfig;
  })();

  return configPromise as Promise<{ authority: string; clientId: string; apiScope: string }>;
}

// Hardcoded OIDC discovery metadata for the Velaro CIAM tenant.
// This bypasses the network fetch of .well-known/openid-configuration so MSAL
// always uses login.velaro.com endpoints regardless of what Microsoft's CIAM
// service returns at runtime (which intermittently contains ciamlogin.com URLs).
// The issuer is kept as ciamlogin.com — that is what Microsoft puts in tokens
// and is already included in ValidIssuers on the server side.
// Captured from: https://login.velaro.com/61de45b3-458d-49a5-913c-501247a6fe4f/v2.0/.well-known/openid-configuration
const VELARO_CIAM_AUTHORITY_METADATA = JSON.stringify({
  token_endpoint: "https://login.velaro.com/61de45b3-458d-49a5-913c-501247a6fe4f/oauth2/v2.0/token",
  token_endpoint_auth_methods_supported: ["client_secret_post", "private_key_jwt", "client_secret_basic", "self_signed_tls_client_auth"],
  jwks_uri: "https://login.velaro.com/61de45b3-458d-49a5-913c-501247a6fe4f/discovery/v2.0/keys",
  response_modes_supported: ["query", "fragment", "form_post"],
  subject_types_supported: ["pairwise"],
  id_token_signing_alg_values_supported: ["RS256"],
  response_types_supported: ["code", "id_token", "code id_token", "id_token token"],
  scopes_supported: ["openid", "profile", "email", "offline_access"],
  issuer: "https://61de45b3-458d-49a5-913c-501247a6fe4f.ciamlogin.com/61de45b3-458d-49a5-913c-501247a6fe4f/v2.0",
  request_uri_parameter_supported: false,
  userinfo_endpoint: "https://graph.microsoft.com/oidc/userinfo",
  authorization_endpoint: "https://login.velaro.com/61de45b3-458d-49a5-913c-501247a6fe4f/oauth2/v2.0/authorize",
  device_authorization_endpoint: "https://login.velaro.com/61de45b3-458d-49a5-913c-501247a6fe4f/oauth2/v2.0/devicecode",
  http_logout_supported: true,
  frontchannel_logout_supported: true,
  end_session_endpoint: "https://login.velaro.com/61de45b3-458d-49a5-913c-501247a6fe4f/oauth2/v2.0/logout",
  claims_supported: ["sub", "iss", "cloud_instance_name", "cloud_instance_host_name", "cloud_graph_host_name", "msgraph_host", "aud", "exp", "iat", "auth_time", "acr", "nonce", "preferred_username", "name", "tid", "ver", "at_hash", "c_hash", "email"],
  tenant_region_scope: "NA",
  cloud_instance_name: "microsoftonline.com",
  cloud_graph_host_name: "graph.windows.net",
  msgraph_host: "graph.microsoft.com",
});

function createMsalConfig(authority: string, clientId: string): Configuration {
  let hostname = "login.velaro.com";
  try {
    hostname = new URL(authority).hostname;
  } catch { /* use default */ }

  return {
    auth: {
      clientId,
      authority,
      knownAuthorities: [hostname],
      authorityMetadata: VELARO_CIAM_AUTHORITY_METADATA,
      redirectUri: `${window.location.origin}/auth/callback`,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: "sessionStorage",
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          if (containsPii) return;
          const prefix = "[MSAL]";
          switch (level) {
            case LogLevel.Error:
              console.error(prefix, message);
              break;
            case LogLevel.Warning:
              console.warn(prefix, message);
              break;
          }
        },
      },
    },
  };
}

// Scope for the V20 messaging API (shared across messaging + admin)
export const apiScope =
  import.meta.env.VITE_ENTRA_API_SCOPE ||
  "api://89bd2fb7-7020-4cfe-a153-1c1ee37903c2/access_as_user";

export const loginRequest = {
  scopes: [apiScope],
};

// MSAL instance — created lazily after config is fetched
let msalInstance: PublicClientApplication | null = null;

/**
 * Get or create the MSAL instance. If runtime config has been fetched, uses that.
 * Otherwise falls back to build-time env vars for backward compatibility.
 */
export function getMsalInstance(): PublicClientApplication | null {
  if (msalInstance) return msalInstance;

  const clientId = runtimeConfig?.clientId || import.meta.env.VITE_ENTRA_CLIENT_ID;
  const authority = runtimeConfig?.authority || import.meta.env.VITE_ENTRA_AUTHORITY;

  if (!clientId || !authority) {
    return null;
  }

  msalInstance = new PublicClientApplication(createMsalConfig(authority, clientId));
  return msalInstance;
}

/**
 * Get MSAL instance after ensuring runtime config is loaded.
 * Use this for login flows where you need the latest config.
 */
export async function getMsalInstanceAsync(): Promise<PublicClientApplication | null> {
  await fetchAuthConfig();
  // Reset instance if authority changed
  if (msalInstance && runtimeConfig) {
    const currentAuthority = (msalInstance as any).config?.auth?.authority;
    if (currentAuthority && currentAuthority !== runtimeConfig.authority) {
      msalInstance = null; // Force re-creation with new authority
    }
  }
  return getMsalInstance();
}

export function isEntraAuthEnabled(): boolean {
  const mode = import.meta.env.VITE_AUTH_MODE;
  return mode === "entra" || mode === "dual";
}
